import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { Constants } from '@/utils'

const { PERIODS } = Constants.EVENT

const DEFAULT_PERIOD = PERIODS[0] // 'live/future'
const DEFAULT_COUNTRY = 'All countries'

// The searchable subset of the home query (no pageNumber / pageSize / view).
// This is the single source of truth that lives in the URL.
export const DEFAULT_SEARCH = {
    searchTerm: '',

    // 'virtual' | 'inside' | undefined — undefined means "no event-type filter".
    // No longer offered by the advanced search panel (its "Event type" is the category);
    // kept so `?eventType=` links keep working (useSearchQuery sends it as `type`).
    eventType: undefined,

    openRegistration: false,

    month: null,

    // 'single' | 'team' | undefined — undefined means "no division filter".
    divisionType: undefined,

    period: DEFAULT_PERIOD,
    country: DEFAULT_COUNTRY,

    categories: [],
}

// Like useSearchParams, but writes always target the home page: on '/' it
// updates the query in place; from any other route it redirects to '/' with
// the new query. Redirects start from EMPTY params (the current page's query
// belongs to that page, not to home) and always push (never replace) so Back
// returns to the page the user searched from.
export const useHomeSearchParams = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const { pathname } = useLocation()
    const navigate = useNavigate()

    if (pathname === '/') return [searchParams, setSearchParams]

    const redirectHome = (updater) => {
        const next = typeof updater === 'function' ? updater(new URLSearchParams()) : updater
        navigate({ pathname: '/', search: String(next) })
    }

    return [searchParams, redirectHome]
}

const readBool = (searchParams, key, defaultValue) => {
    const value = searchParams.get(key)
    return value === null ? defaultValue : value === 'true'
}

// Build a `search` object from a URLSearchParams, re-applying defaults so the
// shape always matches DEFAULT_SEARCH.
export const parseSearchParams = (searchParams) => {
    const monthRaw = searchParams.get('month')
    const period = searchParams.get('period')
    const country = searchParams.get('country')

    return {
        searchTerm: searchParams.get('q') || '',

        eventType: searchParams.get('eventType') || undefined,

        openRegistration: readBool(searchParams, 'openRegistration', false),

        month: monthRaw === null || monthRaw === '' ? null : Number(monthRaw),

        divisionType: searchParams.get('divisionType') || undefined,

        period: period || DEFAULT_PERIOD,
        country: country || DEFAULT_COUNTRY,

        categories: searchParams.getAll('categories'),
    }
}

const setOrDelete = (params, key, value, isDefault) => {
    if (isDefault) params.delete(key)
    else params.set(key, String(value))
}

// Mutate a URLSearchParams to reflect `search`, writing only non-default values
// so the URL stays clean. Returns the same params instance for chaining.
// Only the keys of DEFAULT_SEARCH are touched: unrelated keys already in
// `params` — notably `view=map`, the home list/map toggle — pass through, so a
// filter change or a reset never drops the user out of the map view.
export const writeSearchParams = (params, search) => {
    const term = (search.searchTerm || '').trim()
    setOrDelete(params, 'q', term, term === '')

    setOrDelete(params, 'eventType', search.eventType, !search.eventType)
    setOrDelete(params, 'divisionType', search.divisionType, !search.divisionType)

    setOrDelete(params, 'openRegistration', search.openRegistration, !search.openRegistration)

    setOrDelete(params, 'month', search.month, search.month === null || search.month === undefined)

    setOrDelete(params, 'period', search.period, !search.period || search.period === DEFAULT_PERIOD)
    setOrDelete(params, 'country', search.country, !search.country || search.country === DEFAULT_COUNTRY)

    params.delete('categories')
    ;(search.categories || []).forEach((c) => params.append('categories', c))

    return params
}
